/**
 * Cliente del endpoint /api/v1/tenants/{id}/invites.
 * Backend: app/api/v1/invites.py.
 *
 * Modelo de roles (post-migración 0033): el rol del invite es el "tipo
 * de cuenta" que tendrá el usuario en el tenant. El rol funcional dentro
 * de un proyecto vive en `project_members.role` y se asigna por
 * proyecto, no en el invite.
 */

import { apiFetch } from './client'
import type { UserTenantRole } from './me'

export type InviteStatus = 'pending' | 'consumed' | 'expired' | 'revoked'

export interface InviteRead {
  id: string
  tenant_id: string
  email: string
  role: UserTenantRole
  status: InviteStatus
  is_external: boolean
  /** ISO datetime */
  expires_at: string
  /** ISO datetime */
  created_at: string
  /** ISO datetime cuando el invite fue consumido (null si pending). */
  consumed_at: string | null
}

export interface CreateInviteBody {
  email: string
  /** Default backend: 'member'. */
  role?: UserTenantRole
  /** Auto-derivado por el backend si role=client (siempre true). Para
   * Para editor/reviewer/admin no enviarlo (default false). */
  is_external?: boolean
  /** Required cuando role='admin'. */
  confirm_admin?: boolean
}

export async function listInvites(
  accessToken: string,
  tenantId: string,
  filters?: { status?: InviteStatus },
): Promise<InviteRead[]> {
  const qs = filters?.status ? `?status_filter=${filters.status}` : ''
  return apiFetch<InviteRead[]>(
    `/api/v1/tenants/${tenantId}/invites${qs}`,
    { accessToken, activeTenantId: tenantId },
  )
}

export async function createInvite(
  accessToken: string,
  tenantId: string,
  body: CreateInviteBody,
): Promise<InviteRead> {
  return apiFetch<InviteRead>(`/api/v1/tenants/${tenantId}/invites`, {
    accessToken,
    activeTenantId: tenantId,
    method: 'POST',
    body,
  })
}

export async function revokeInvite(
  accessToken: string,
  tenantId: string,
  inviteId: string,
): Promise<void> {
  return apiFetch<void>(
    `/api/v1/tenants/${tenantId}/invites/${inviteId}`,
    { accessToken, activeTenantId: tenantId, method: 'DELETE' },
  )
}
